import BootstrapModal from 'bootstrap/js/dist/modal.js';
import Swiper from 'swiper';
import { A11y, Keyboard, Navigation } from 'swiper/modules';

/** Bootstrap modal event extended with the element that triggered the modal. */
type BootstrapModalEvent = Event & { relatedTarget: HTMLElement | null };

/**
 * Controls a single image gallery instance, wiring a Bootstrap modal to a
 * Swiper carousel so thumbnails open the modal at the correct slide index.
 */
class ImageGallery {
  /** The element that last triggered the modal open, used to restore focus on close. */
  triggerElement: HTMLElement | null = null;
  /** The root gallery element (`[data-hook=imageGallery]`). */
  gallery: HTMLElement;
  /** The Bootstrap modal element (`[data-hook=imageGalleryModal]`). */
  modalEl: HTMLElement | null = null;
  /** The `.modal-body` element, toggled between opaque and transparent during transitions. */
  modalBody: HTMLElement | null = null;
  /** The Swiper root element inside the modal. */
  swiperEl: HTMLElement | null = null;
  /** The Swiper instance, created lazily on first modal open. */
  swiper: Swiper | null = null;
  /** The Bootstrap modal instance, used to close the modal on backdrop clicks. */
  modal: BootstrapModal | null = null;
  /** Visually-hidden `role="status"` region that announces the active slide to screen readers. */
  statusEl: HTMLElement | null = null;
  /** Last announced message, used to avoid announcing the same slide twice (e.g. open + slideChange). */
  lastAnnouncement = '';

  /**
   * @param gallery - The root gallery DOM element.
   */
  constructor(gallery: HTMLElement) {
    this.gallery = gallery;

    this.modalEl = this.gallery.querySelector<HTMLElement>('[data-hook=imageGalleryModal]');
    if (this.modalEl === null) {
      console.warn('[ImageGallery] No modal found for gallery:', this.gallery);
      return;
    }

    // Move the modal up to <body>. The widget-wrapper has `container-type: inline-size`, which makes
    // it a containing block for the modal's `position: fixed` — trapping and clipping it inside the
    // widget. As a direct child of <body> the modal escapes that containment and uses Bootstrap's
    // normal top-level stacking, so no z-index/`position: unset` hacks are needed. (We query the modal
    // from the gallery first, above, so the reference is captured before the move.)
    document.body.appendChild(this.modalEl);

    this.swiperEl = this.modalEl.querySelector<HTMLElement>('[data-hook=imageGallerySwiper]');
    if (this.swiperEl === null) {
      console.warn('[ImageGallery] No swiper found for gallery:', this.gallery);
      return;
    }

    this.statusEl = this.modalEl.querySelector<HTMLElement>('[data-hook=imageGalleryStatus]');

    this.modal = new BootstrapModal(this.modalEl);
    this.modalBody = this.modalEl.querySelector<HTMLElement>('.modal-body');
    if (!this.modal || this.modalBody === null) {
      console.warn('[ImageGallery] No modal body found for gallery:', this.gallery);
    }
  }

  /** Attaches Bootstrap modal event listeners and sets the initial hidden state. */
  init(): void {
    if (!this.modalEl) return;

    this.handleHideModal();

    this.modalEl.addEventListener('shown.bs.modal', (event) => this.handleShowModal(event as BootstrapModalEvent));
    this.modalEl.addEventListener('hide.bs.modal', () => this.handleHideModal());

    // Close the modal when the surrounding "backdrop" area is clicked. Bootstrap's built-in
    // backdrop dismiss only fires on clicks on the bare `.modal` element, but the transparent
    // `.modal-content`/Swiper fills the dialog, so the dark area around the image reads as the
    // backdrop yet isn't. Dismiss on clicks anywhere in `.modal-content` except the image, the
    // prev/next controls, the close button, and the caption region.
    const modalContent = this.modalEl.querySelector<HTMLElement>('.modal-content');
    modalContent?.addEventListener('click', (event) => this.handleBackdropClick(event));
  }

  /**
   * Closes the modal when the empty area around the image (the visual "backdrop") is clicked.
   * Ignores clicks on the image, the Swiper navigation arrows, the close button, and the
   * caption scroll region so those remain interactive.
   *
   * @param event - The click event bubbled up to `.modal-content`.
   */
  handleBackdropClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('img, .swiper-button-prev, .swiper-button-next, .btn-close, .swiper-slide__caption-scroll')) {
      return;
    }
    this.modal?.hide();
  }

  /** Creates the Swiper instance the first time the modal is opened. No-op if already initialised. */
  initSwiper(): void {
    if (this.swiper || this.swiperEl === null) return;

    this.swiper = new Swiper(this.swiperEl, {
      modules: [Navigation, A11y, Keyboard],
      slidesPerView: 1,
      slidesPerGroup: 1,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      a11y: {
        enabled: true,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      on: {
        slideChange: () => this.announceActiveSlide(),
      },
    });
  }

  /**
   * Initialises Swiper (if needed), enables it, and slides to the index stored
   * on the triggering element's `data-slide-to-index` attribute.
   *
   * @param event - The Bootstrap `shown.bs.modal` event.
   */
  handleShowModal(event: BootstrapModalEvent): void {
    this.triggerElement = event.relatedTarget;

    this.initSwiper();

    if (!this.swiper) return;

    const slideToIndex = Number(this.triggerElement?.dataset.slideToIndex ?? 0);

    this.swiper.enable();
    this.swiper.update();
    this.swiper.updateSize();

    // Swiper's A11y module marks the wrapper aria-live="polite", which would announce the active
    // slide alongside our own status region — a double read. Remove it so our region (image number
    // + caption) is the single, controlled announcement. Done after update() in case it re-adds it.
    this.swiper.wrapperEl?.removeAttribute('aria-live');

    this.swiper.slideTo(slideToIndex, 0);
    this.modalBody?.classList.remove('opacity-0');
    // Move focus onto a focusable control — the close button — rather than the non-interactive
    // active slide. Runs on `shown.bs.modal`, so it wins over Bootstrap's own initial focus.
    this.modalEl?.querySelector<HTMLButtonElement>('.btn-close')?.focus();

    // Announce the opened slide. slideTo above fires slideChange (which also announces) only when
    // the index actually changes; announceActiveSlide de-dupes so opening at index 0 still speaks.
    this.announceActiveSlide();
  }

  /**
   * Returns focus to the trigger element, disables Swiper, and hides the modal
   * body to prevent a visible slide-transition when the modal reopens.
   *
   */
  handleHideModal(): void {
    this.triggerElement?.focus();

    // Prevents controls accidentally being triggered.
    this.swiper?.disable();

    // Prevents the user from seeing the wrong slide while it's sliding to it.
    this.modalBody?.classList.add('opacity-0');

    // Reset the announcement state + clear the region so the next open re-announces its slide
    // (even if it's the same index/message as the previous open).
    this.lastAnnouncement = '';
    if (this.statusEl) this.statusEl.textContent = '';
  }

  /**
   * Announces the active slide to screen readers via the `role="status"` region: the localized
   * "Image X of Y" indicator (reused from the visible page indicator) plus the caption, if any.
   * De-dupes consecutive identical messages so open + slideChange don't double-announce.
   */
  announceActiveSlide(): void {
    if (!this.statusEl || !this.swiper) return;

    const slide = this.swiper.slides[this.swiper.activeIndex] as HTMLElement | undefined;
    if (!slide) return;

    const indicator = slide.querySelector('.swiper-slide__page-indicator')?.textContent?.trim() ?? '';
    const caption = slide.querySelector('.swiper-slide__caption-content')?.textContent?.trim() ?? '';
    const message = caption ? `${indicator}. ${caption}` : indicator;

    if (!message || message === this.lastAnnouncement) return;

    this.lastAnnouncement = message;
    this.statusEl.textContent = message;
  }
}

/** Public API shape for the `ImageGalleries` singleton. */
type ImageGalleries = {
  /** Discovers all `[data-hook=imageGallery]` elements and initializes each one. */
  init: () => void;
};

/** Singleton that discovers and initialises all image galleries on the page. */
const ImageGalleries: ImageGalleries = {
  init() {
    const imageGalleries: NodeListOf<HTMLElement> = document.querySelectorAll('[data-hook=imageGallery]');
    if (!imageGalleries.length) return;

    imageGalleries.forEach((gallery) => {
      new ImageGallery(gallery).init();
    });
  },
};

export default ImageGalleries;
